import { LCAoMService } from "@/types/LCAoM/LCAoMService";
import { TaskProgress } from "@/types/LCAoM/TaskProgress";
import { Co2MaterialType, NodeMaterialDto } from "@api/apiClient";
import { useUpsertNodeMaterials } from "@api/models";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import { useCallback } from "react";

interface UseAssignMaterialsParams {
  assignMaterialDlgRef: React.MutableRefObject<any>;
  clearSelectedNodes: () => Promise<void>;
  addToCo2MaterialTypeLookup: (materialType: Co2MaterialType) => void;
  lcaoMService: LCAoMService;
  onSuccess?: () => Promise<void> | void;
  setOpenAssignMaterialDlg: (open: boolean) => void;
  setServiceVersion: (version: number) => void;
  setTaskProgress: (progress: TaskProgress | null) => void;
  updateIndexedDBMaterials: (scenarioId: string) => Promise<void>;
}

interface UseAssignMaterialsResult {
  handleAssignMaterials: (selectedCo2MaterialType?: Co2MaterialType) => Promise<void>;
  isAssignMaterialsPending: boolean;
  isUpsertNodeMaterialsPending: boolean;
}

export const useAssignMaterials = ({
  assignMaterialDlgRef,
  clearSelectedNodes,
  addToCo2MaterialTypeLookup,
  lcaoMService,
  onSuccess,
  setOpenAssignMaterialDlg,
  setServiceVersion,
  setTaskProgress,
  updateIndexedDBMaterials,
}: UseAssignMaterialsParams): UseAssignMaterialsResult => {
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const mutateUpsertNodeMaterials = useUpsertNodeMaterials();

  const handleAssignMaterials = useCallback(
    async (selectedCo2MaterialType?: Co2MaterialType) => {
      if (!selectedCo2MaterialType) {
        assignMaterialDlgRef.current?.setIsLoading(false);
        setOpenAssignMaterialDlg(false);
        return;
      }

      const selectedScenarioId = selectionManager.selectedScenarioId;
      if (!selectedScenarioId) {
        console.error("No scenario selected");
        assignMaterialDlgRef.current?.setIsLoading(false);
        setOpenAssignMaterialDlg(false);
        return;
      }

      // Seed the lookup with the picked material so the grid can render its name/unit
      // immediately after lcaoMService updates, without waiting for nodeMaterials to refetch.
      addToCo2MaterialTypeLookup(selectedCo2MaterialType);

      // Close dialog immediately
      assignMaterialDlgRef.current?.setIsLoading(false);
      setOpenAssignMaterialDlg(false);

      // Show progress overlay (simple show/hide - no updates during processing for speed)
      setTaskProgress({
        taskId: "assignMaterials",
        label: "Assigning materials...",
        phase: "processing",
        processed: 0,
        total: 0,
        percent: 0,
      });

      // Allow React to flush the dialog close and show overlay
      await new Promise((resolve) => setTimeout(resolve, 0));

      let computedMaterials: NodeMaterialDto[] = [];

      try {
        // Client-side calculation with chunked processing for large datasets
        // LCAoMService gets nodeSupplementals from ModelsDataService internally
        console.log("[handleAssignMaterials] Starting calculation...");
        computedMaterials = await lcaoMService.assignMaterialToSelection(
          selectedCo2MaterialType,
          selectedScenarioId,
          //setTaskProgress // Progress updates for large datasets
        );
        console.log("[handleAssignMaterials] Calculation complete, materials:", computedMaterials.length);
      } catch (error) {
        console.error("[handleAssignMaterials] Error:", error);
        console.log("[handleAssignMaterials] Hiding overlay");
        setTaskProgress(null);
        return;
      }

      // Hide progress overlay once the calculation + grid updates are done
      console.log("[handleAssignMaterials] Hiding overlay");
      setTaskProgress(null);

      // Sync version to trigger lookup re-derivation
      setServiceVersion(lcaoMService.version);

      await clearSelectedNodes();

      if (computedMaterials.length > 0 && appStore.autoSaveMaterialAssignments) {
        // Update IndexedDB and React Query cache immediately (optimistic update)
        await updateIndexedDBMaterials(selectedScenarioId);

        // Wait for server mutation so follow-up refetch happens after completion
        const patchRequest = lcaoMService.generatePatchRequest(selectedScenarioId);
        await mutateUpsertNodeMaterials.mutateAsync(patchRequest);
      }

      await onSuccess?.();
    },
    [
      addToCo2MaterialTypeLookup,
      appStore.autoSaveMaterialAssignments,
      assignMaterialDlgRef,
      clearSelectedNodes,
      lcaoMService,
      onSuccess,
      selectionManager.selectedScenarioId,
      setOpenAssignMaterialDlg,
      setServiceVersion,
      setTaskProgress,
      updateIndexedDBMaterials,
      mutateUpsertNodeMaterials,
    ]
  );

  return {
    handleAssignMaterials,
    isAssignMaterialsPending: mutateUpsertNodeMaterials.isPending,
    isUpsertNodeMaterialsPending: mutateUpsertNodeMaterials.isPending,
  };
};
